import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppId } from "../../app/appTypes";
import { unwrapResult } from "@reduxjs/toolkit";
import { showError, showSuccess } from "../notifications/notificationsSlice";
import Button from '@mui/material/Button';
import Fab from '@mui/material/Fab';
import AddIcon from '@mui/icons-material/Add';
import { Link } from 'react-router-dom';
import { Box, ButtonGroup, Container, Dialog, DialogActions, DialogContent, DialogContentText, Tab } from '@mui/material';
import { Localized, useLocalization } from '@fluent/react';
import { AppLoader } from '../../app/AppLoader';
import { FlexDiv } from '../../app/Utils';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { deleteSupplierDevice, loadSupplierDevices, selectAllSupplierDevices, selectSupplierDevices, SupplierDevice } from "./supplierDeviceSlice";
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { ImportPanel } from './ImportPanel';
import { useNavigate } from 'react-router-dom';
import { ErrorContainer } from '../../app/ErrorContainer';
import { LoadingContainer } from '../../app/LoadingContainer';
import { getStructureName, StructureName } from '../device/Structure';
import { selectContextStructure } from '../structure/contextStructureSlice';
import { selectAllStructure, selectStructureAndChildren } from '../device/structureRelationshipSlice'

export const SupplierDevices = () => {
    const { loaded } = useAppSelector(selectSupplierDevices)
    const { l10n } = useLocalization()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const allSupplierDevices = useAppSelector(state => selectAllSupplierDevices(state))
    const [deleting, setDeleting] = useState(false)
    const [itemToDelete, setItemToDelete] = useState<AppId | undefined>(undefined)
    const [_, setSupplierDevices] = useState<SupplierDevice[]>([]);
    const [isError, setIsError] = useState<boolean>(false)
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [refreshData, setRefreshData] = useState(false); // Add this state
    const { data: structure } = useAppSelector(selectContextStructure)
    const relationship = useAppSelector(state => {
        if (structure && structure.mode === 'manual') {
            return selectStructureAndChildren(state, structure.structId)
        } else {
            return selectAllStructure(state)
        }
    })

    const supplierDevices = useMemo(() => {
        return allSupplierDevices.filter(({ structureId }) => relationship.has(structureId));
    }, [allSupplierDevices, relationship])

    const handleDeleteClick = useCallback((id: AppId) => {
        setItemToDelete(id)
    }, [setItemToDelete])

    const handleCancelDelClick = useCallback(() => {
        setItemToDelete(undefined)
    }, [setItemToDelete])

    const handleConfirmDelClick = async () => {
        if (itemToDelete) {
            setDeleting(true)
            try {
                unwrapResult(await dispatch(deleteSupplierDevice(itemToDelete)))
                dispatch(showSuccess("deleted"))
            } catch (error) {
                dispatch(showError("error"))
            }
            setItemToDelete(undefined)
            setDeleting(false)
        }
    }

    useEffect(() => {
        setIsLoading(true)

        dispatch(loadSupplierDevices())
            .then(unwrapResult)
            .then(setSupplierDevices)
            .catch((error) => { console.error(error); setIsError(true); setError(error); })
            .finally(() => setIsLoading(false))
    }, [refreshData]);

    type FormTabs = "list" | "attachments"
    const [tab, setTab] = useState<FormTabs>("list");
    const handleTabChange = useCallback((_, newTab) => {
        setTab(newTab)
    }, [])

    const handleGoToClick = (id) => {
        navigate(`/supplierdevices/edit/${id}`)
    };


    const columns: GridColDef[] =
        [
            {
                field: 'name',
                headerName: l10n.getString('devices-name', null, 'Nazwa'),
                flex: 1
            },
            {
                field: 'structureId',
                flex: 1,
                headerName: l10n.getString('devices-type', null, 'Typ urządzenia'),
                valueGetter: (_, row) => {
                    return getStructureName(row.structureId)
                }
            },
            {
                field: 'actions',
                width: 200,
                renderCell: (params) => {
                    return (
                        <ButtonGroup>
                            <Button onClick={() => handleGoToClick(params.row.id)} color="secondary" variant="outlined">
                                <Localized id="edit">
                                    <span>Edytuj</span>
                                </Localized>
                            </Button>
                            <Button color="primary" onClick={() => handleDeleteClick(params.row.id)}>
                                <Localized id="delete">
                                    <span>Usuń</span>
                                </Localized>
                            </Button>
                        </ButtonGroup>
                    )
                }
            }
        ];

    if (isError) {
        return <>
            <ErrorContainer error={error} />
        </>
    }

    if (isLoading) {
        return <>
            <LoadingContainer />
        </>
    }
    return (
        <>
            <Container maxWidth={false}>
                <TabContext value={tab}>
                    <TabList onChange={handleTabChange}>
                        <Tab
                            key={0}
                            value={"list"}
                            label={<Localized id="">Lista urządzeń</Localized>} />
                        <Tab
                            key={1}
                            value={"attachments"}
                            label={<Localized id="">Import</Localized>} />
                    </TabList>

                    <TabPanel value="list">
                        <Box sx={{ width: '100%' }}>

                            <div style={{ flexGrow: 1 }}>
                                <DataGrid autoHeight rows={supplierDevices} columns={columns} onRowDoubleClick={({ row }) => handleGoToClick(row.id)} />
                            </div>
                            <FlexDiv>
                                <Fab component={Link} to="/supplierdevices/create" color="secondary" sx={{ marginTop: 2 }}>
                                    <AddIcon />
                                </Fab>
                            </FlexDiv>
                        </Box>
                    </TabPanel>

                    <TabPanel value="attachments">
                        <ImportPanel onSucces={function (): void {
                           setRefreshData(prev => !prev)
                        } } />
                    </TabPanel>

                </TabContext>
                <Dialog open={itemToDelete !== undefined}>
                    <DialogContent>
                        <DialogContentText>
                            <Localized id="confirm-delete">
                                <span>Czy napewno chcesz usunąć?</span>
                            </Localized>
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleConfirmDelClick}>
                            <Localized id="yes"><span>Tak</span></Localized>
                        </Button>
                        <Button onClick={handleCancelDelClick}>
                            <Localized id="no"><span>Nie</span></Localized>
                        </Button>
                    </DialogActions>
                </Dialog>
                <AppLoader open={deleting} />

            </Container>
        </>
    );
}
